/**
 * TmSummary.jsx — TM 실적 요약 메인 컴포넌트
 *
 * 연/월 선택으로 결과유형별 콜 통계(차트+테이블), 드릴다운 콜 목록,
 * 월별 달력(콜 건수·계약 배지)을 한 페이지에서 제공합니다.
 *
 * API:
 *   GET /api/tm/summary?start_date=&end_date=   → 결과유형별 통계
 *   GET /api/tm/calls?year=Y&month=M            → 달력용 월별 콜 전체
 *   GET /api/tm/summary/kpi?year=Y&month=M      → 계약 건수 + 상품별 장수
 *
 * dark mode: CARD/TH/TD/NAV_BTN/NAV_SELECT 모두 CSS 변수 사용
 *
 * 하위 컴포넌트:
 *   SummaryHeaderNav     — 제목 + 연/월 네비게이션
 *   SummaryKpiRow        — KPI 카드 행(총 콜·계약·신규·재주문·총 장수)
 *   FeedbackStatsPanel   — 결과유형별 차트 + 요약 테이블
 *   DrilldownCallList    — 결과유형 클릭 시 콜 상세 목록
 *   CalendarView         — 월별 달력 뷰
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '../../../components/Toast';
import {
  useIsMobile, nowKst, monthRange,
  getPineTotalFromItems, getSimpleTotalFromItems, CARD,
} from './utils';
import SummaryHeaderNav   from './SummaryHeaderNav';
import SummaryKpiRow      from './SummaryKpiRow';
import FeedbackStatsPanel from './FeedbackStatsPanel';
import { useSummaryData }  from './useSummaryData';
import DrilldownCallList  from './DrilldownCallList';
import CalendarView       from './CalendarView';

const defaultBuildSummaryPath = ({ dateRange }) => {
  const params = new URLSearchParams({ start_date: dateRange.start, end_date: dateRange.end });
  return `/api/tm/summary?${params.toString()}`;
};

const defaultBuildCallsPath = ({ year, month, feedback, dateRange }) => {
  const params = new URLSearchParams();
  if (year) params.set('year', year);
  if (month) params.set('month', month);
  if (feedback) params.set('feedback', feedback);
  if (dateRange?.start) params.set('start_date', dateRange.start);
  if (dateRange?.end) params.set('end_date', dateRange.end);
  return `/api/tm/calls?${params.toString()}`;
};

const defaultBuildKpiPath = ({ year, month }) => {
  const params = new URLSearchParams({ year, month });
  return `/api/tm/summary/kpi?${params.toString()}`;
};

const defaultBuildCalendarContractsPath = ({ year, month }) => {
  const params = new URLSearchParams({ year, month });
  return `/api/tm/calendar-contracts?${params.toString()}`;
};

// 월간 점수 — 경로 빌더가 null 이면 점수를 아예 조회하지 않는다(마케터 정산서 등 노출 금지 화면).
const defaultBuildScorePath = ({ year, month }) => {
  const params = new URLSearchParams({ year, month });
  return `/api/tm/summary/score?${params.toString()}`;
};

export default function TmSummary({
  initialYear,
  initialMonth,
  title = '요약',
  buildSummaryPath = defaultBuildSummaryPath,
  buildCallsPath = defaultBuildCallsPath,
  buildKpiPath = defaultBuildKpiPath,
  buildCalendarContractsPath = defaultBuildCalendarContractsPath,
  buildScorePath = defaultBuildScorePath,
  renderTotalTicketsExtra = null,
  lockedMonth = false,
}) {
  const toast    = useToast();
  const isMobile = useIsMobile();

  // ── 연/월 상태 ─────────────────────────────────────────────────────────────
  const now = nowKst();
  const [year,  setYear]  = useState(initialYear || now.getFullYear());
  const [month, setMonth] = useState(initialMonth || now.getMonth() + 1);

  // ── 드릴다운 선택 상태 ─────────────────────────────────────────────────────
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  useEffect(() => {
    if (!lockedMonth) return;
    if (initialYear) setYear(Number(initialYear));
    if (initialMonth) setMonth(Number(initialMonth));
    setSelectedFeedback(null);
  }, [initialMonth, initialYear, lockedMonth]);

  // 드릴다운 카드 마운트 시 자동 스크롤
  const drilldownRef = useRef(null);
  useEffect(() => {
    if (selectedFeedback && drilldownRef.current) {
      setTimeout(() => drilldownRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 0);
    }
  }, [selectedFeedback]);

  const dateRange = useMemo(() => monthRange(year, month), [year, month]);

  // ── 월 이동 핸들러 ─────────────────────────────────────────────────────────

  function handlePrev() {
    setSelectedFeedback(null);
    if (month === 1) { setYear((y) => y - 1); setMonth(12); }
    else             { setMonth((m) => m - 1); }
  }
  function handleNext() {
    setSelectedFeedback(null);
    if (month === 12) { setYear((y) => y + 1); setMonth(1); }
    else              { setMonth((m) => m + 1); }
  }
  function handleToday() {
    const n = nowKst();
    setYear(n.getFullYear());
    setMonth(n.getMonth() + 1);
    setSelectedFeedback(null);
  }
  function handleYearChange(value) {
    setYear(value);
    setSelectedFeedback(null);
  }
  function handleMonthChange(value) {
    setMonth(value);
    setSelectedFeedback(null);
  }

  // ── API 조회 (useSummaryData 훅) ──────────────────────────────────────────
  const clearFeedback = useCallback(() => setSelectedFeedback(null), []);
  const { stats, total, loading, calCalls, calLoading, kpiData, kpiLoading, scoreData } = useSummaryData({
    year, month, dateRange, toast,
    buildSummaryPath, buildCallsPath, buildKpiPath, buildScorePath,
    onBeforeSummary: clearFeedback,
  });

  function handleBarClick(feedback) {
    setSelectedFeedback((prev) => (prev === feedback ? null : feedback));
  }

  // ── KPI 파생 값 (kpiData: contracts 테이블 기준) ────────────────────────────
  // 신규 계약 건수 (재주문 제외 — 재주문은 계약율에 영향 없음)
  const contractCount    = kpiData?.newCount     ?? 0;
  // 외부직원 여부 (DOT/LNJ: 단순 합산, 파인그룹: 가중치 공식)
  const isExternal       = kpiData?.isExternal   ?? false;
  // 신규 상품별 장수
  const newBreakdown     = kpiData?.newItems     ?? [];
  // 재주문 상품별 장수
  const reorderBreakdown = kpiData?.reorderItems ?? [];
  // 총 장수: 외부(DOT/LNJ)=단순 합산, 파인그룹=상품 환산장수 기준
  const allItems         = [...newBreakdown, ...reorderBreakdown];
  const totalTickets     = isExternal
    ? getSimpleTotalFromItems(allItems)
    : Number(kpiData?.totalTickets ?? getPineTotalFromItems(allItems));

  // ── 렌더 ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'grid', gap: 14, overflow: 'hidden', minWidth: 0 }}>

      {/* ── 헤더 카드: 제목 + 네비게이션 + KPI ── */}
      <div style={{ ...CARD, padding: isMobile ? '12px 14px' : '16px 20px' }}>
        <SummaryHeaderNav
          title={title}
          isMobile={isMobile}
          lockedMonth={lockedMonth}
          year={year}
          month={month}
          baseYear={now.getFullYear()}
          onPrev={handlePrev}
          onNext={handleNext}
          onToday={handleToday}
          onYearChange={handleYearChange}
          onMonthChange={handleMonthChange}
        />

        {/* KPI 요약: 총 콜 / 계약(신규) / 신규 상품별 장수 / 재주문 상품별 장수 / 총 장수 */}
        <SummaryKpiRow
          isMobile={isMobile}
          loading={loading}
          kpiLoading={kpiLoading}
          calLoading={calLoading}
          total={total}
          contractCount={contractCount}
          newBreakdown={newBreakdown}
          reorderBreakdown={reorderBreakdown}
          totalTickets={totalTickets}
          renderTotalTicketsExtra={renderTotalTicketsExtra}
          kpiData={kpiData}
          scoreData={scoreData}
          year={year}
          month={month}
        />
      </div>

      {/* ── 차트 + 요약 테이블 카드 ── */}
      <FeedbackStatsPanel
        stats={stats}
        total={total}
        selectedFeedback={selectedFeedback}
        onSelect={handleBarClick}
        loading={loading}
        isMobile={isMobile}
      />

      {/* ── 드릴다운 카드 ── */}
      {!loading && selectedFeedback && (
        <div ref={drilldownRef} style={{ ...CARD, borderLeft: '3px solid #2563eb', padding: isMobile ? '12px 14px' : '16px 20px' }}>
          <DrilldownCallList
            feedback={selectedFeedback}
            dateRange={dateRange}
            year={year}
            month={month}
            buildCallsPath={buildCallsPath}
            onClose={() => setSelectedFeedback(null)}
          />
        </div>
      )}

      {/* ── 달력 카드 ── */}
      <div style={{ ...CARD, padding: isMobile ? '12px 10px' : '16px 20px', overflow: 'hidden' }}>
        <div style={{ marginBottom: 14 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{year}년 {month}월</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>달력</span>
        </div>
        <CalendarView
          year={year}
          month={month}
          calls={calCalls}
          loading={calLoading}
          buildCalendarContractsPath={buildCalendarContractsPath}
        />
      </div>
    </div>
  );
}
